import React, { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { markListingAsSoldRequest, unmarkListingAsSoldRequest, reserveListingRequest, unreserveListingRequest, relistListingRequest, unmarkListingAsStaleRequest, checkRelistedListingRequest, boostListingRequest, cancelListingRequest } from 'actions/listingActions'
import { DeleteSvg } from 'components/atoms/ListingsTable/DeleteIcon'
import Loader from 'components/atoms/ListingsTable/Loader'
import StatusTag from 'components/atoms/ListingsTable/StatusTag'
import MoreOptionsDropdown from '../MoreOptionsDropdown'
import DeleteListingConfirmationDialog from '../DeleteListingConfirmationDialog'
import { onBfcacheRestore } from '../../../utils/bfcacheHelpers'
import { myListingsIconUrl } from '../../../utils/staticAssets'
import {
  trackEditListing,
  trackRelistListing,
  trackBoostListing,
  trackMarkAsSold,
  trackUnmarkAsSold,
  trackHoldListing,
  trackUnholdListing,
  trackUnmarkAsStale,
  trackDeleteListing
} from '../../../analytics/events/seller'

class MobileTableRow extends Component {
  constructor (props) {
    super(props)
    this.state = {
      gridTemplateColumns: '110px 1fr',
      isUpdating: false,
      intervalVariable: null,
      displayErrorMessage: false,
      errorMessage: 'Oops something went wrong, please reload the page or contact us at sellersupport@vinterior.co if the error persists',
      deleteDialogOpen: false,
      deleteError: null
    }

    this.handleOpenDeleteDialog = this.handleOpenDeleteDialog.bind(this)
    this.handleCloseDeleteDialog = this.handleCloseDeleteDialog.bind(this)
    this.handleConfirmDelete = this.handleConfirmDelete.bind(this)
    this.stopListeningForBfcacheRestore = null
  }

  componentDidMount () {
    this.stopListeningForBfcacheRestore = onBfcacheRestore(() => {
      clearInterval(this.state.intervalVariable)
      this.setState({ isUpdating: false, intervalVariable: null })
    })
  }

  componentWillUnmount () {
    if (this.stopListeningForBfcacheRestore) this.stopListeningForBfcacheRestore()
    clearInterval(this.state.intervalVariable)
  }

  handleOpenDeleteDialog () { this.setState({ deleteDialogOpen: true, deleteError: null }) }

  handleCloseDeleteDialog () { this.setState({ deleteDialogOpen: false, deleteError: null }) }

  handleConfirmDelete () {
    trackDeleteListing('seller-dashboard-listings')
    this.props.dispatch(cancelListingRequest(this.props.listing.id, this.props.selectedFilter))
      .then((data) => {
        if (data && data.success) {
          this.setState({ deleteDialogOpen: false, deleteError: null })
        } else {
          this.setState({ deleteError: (data && data.error) || this.state.errorMessage })
        }
      })
  }

  // On mobile every action lives in the labelled "More" menu (the inline icons
  // had no labels). Builds the status-appropriate action items.
  buildMenuActions (listing) {
    const { id, status, p2p, isBoosted, boostsLeft, relisted } = listing
    const actions = []
    const s3 = (name) => <img className='w-20' src={myListingsIconUrl(name)} alt='' />
    const run = (handler, track) => {
      this.setState({ isUpdating: true })
      if (track) track('seller-dashboard-listings')
      handler()
    }

    if (status === 'Published' && !isBoosted && boostsLeft && this.props.canBoostListings && listing.price >= 600) {
      actions.push({ key: 'boost', label: 'Boost', icon: s3('boost_listing.svg'), onClick: () => { this.setState({ isUpdating: true }); this.handleBoostListing(id); trackBoostListing('seller-dashboard-listings', id) } })
    }
    if (['Published', 'On hold', 'Auto marked as stale'].includes(status)) {
      actions.push({ key: 'mark-as-sold', label: 'Mark as sold', icon: s3('shopping_cart_crossed.svg'), onClick: () => run(() => this.handleMarkAsSold(id), trackMarkAsSold) })
    }
    if (status === 'Published' && !p2p) {
      actions.push({ key: 'reserve', label: 'Mark as unavailable', icon: s3('lock.svg'), onClick: () => run(() => this.handleReserveListing(id), trackHoldListing) })
    }
    if (status === 'On hold') {
      actions.push({ key: 'unreserve', label: 'Mark as available', icon: s3('lock_open.svg'), onClick: () => run(() => this.handleUnreserveListing(id), trackUnholdListing) })
    }
    if (status === 'Auto marked as stale') {
      actions.push({ key: 'unmark-stale', label: 'Mark as available', icon: s3('check_circle.svg'), onClick: () => run(() => this.handleMarkAsAvailable(id), trackUnmarkAsStale) })
    }
    if (status === 'Auto marked as sold') {
      actions.push({ key: 'unmark-sold', label: 'Unmark as sold', icon: s3('shopping_cart.svg'), onClick: () => run(() => this.handleUnmarkAsSold(id), trackUnmarkAsSold) })
    }
    if (status === 'Action required' && !p2p) {
      actions.push({ key: 'add-photo', label: 'Add photos', icon: s3('add_photo.svg'), href: '/listings/' + id + '/photos/upload' })
      actions.push({ key: 'update-price', label: 'Update price', icon: s3('update_price.svg'), href: '/listings/' + id + '/edit?#price-section' })
    }
    if (['Draft', 'Awaiting approval'].includes(status)) {
      actions.push({ key: 'edit', label: 'Edit listing', icon: s3('pencil.svg'), href: '/listings/' + id + '/edit', onClick: () => trackEditListing('seller-dashboard-listings') })
    }
    if (status === 'Sold' && !relisted) {
      actions.push({ key: 'relist', label: 'Relist', icon: s3('shopping_cart.svg'), onClick: () => { this.setState({ isUpdating: true }); this.handleRelistListing(id); this.handleCheckRelistedListing(id); trackRelistListing('seller-dashboard-listings') } })
    }
    if (['Published', 'On hold', 'Auto marked as stale'].includes(status) || (status === 'Action required' && !p2p)) {
      actions.push({ key: 'delete', label: 'Delete listing', icon: <DeleteSvg size={20} />, onClick: this.handleOpenDeleteDialog })
    }

    return actions
  }

  componentDidUpdate (prevProps) {
    if (prevProps.listing !== this.props.listing && this.state.isUpdating) {
      this.setState({ isUpdating: false })
    }
    if (prevProps.listing !== this.props.listing && this.props.listing.relisted) {
      clearInterval(this.state.intervalVariable)
    }
  }

  // actions start

  handleBoostListing (listingId) { this.props.dispatch(boostListingRequest(listingId, this.props.selectedFilter)) }

  handleMarkAsSold (listingId) { this.props.dispatch(markListingAsSoldRequest(listingId, this.props.selectedFilter)) }

  handleUnmarkAsSold (listingId) { this.props.dispatch(unmarkListingAsSoldRequest(listingId, this.props.selectedFilter)) }

  handleReserveListing (listingId) { this.props.dispatch(reserveListingRequest(listingId, this.props.selectedFilter)) }

  handleUnreserveListing (listingId) { this.props.dispatch(unreserveListingRequest(listingId, this.props.selectedFilter)) }

  handleRelistListing (listingId) { this.props.dispatch(relistListingRequest(listingId, this.props.selectedFilter)) }

  handleCheckRelistedListing (listingId) {
    const startTime = new Date().getTime()
    const oneMinInMs = 60000
    const intervalVariable = setInterval(() => {
      this.props.dispatch(checkRelistedListingRequest(listingId, this.props.selectedFilter))
      const timeSinceStartTime = new Date().getTime() - startTime

      if (timeSinceStartTime > oneMinInMs) {
        clearInterval(this.state.intervalVariable)
        this.setState({ isUpdating: false })

        if (!this.props.listing.relisted) {
          this.setState({ displayErrorMessage: true })
        }
      }
    }, 3000)
    this.setState({ intervalVariable: intervalVariable })
  }

  handleMarkAsAvailable (listingId) { this.props.dispatch(unmarkListingAsStaleRequest(listingId, this.props.selectedFilter)) }

  // actions end

  renderCTAs (listing) {
    if (this.state.displayErrorMessage) return null

    const { status } = listing
    const actions = this.buildMenuActions(listing)
    // The dropdown also renders default Edit/View/Update-price links for the
    // relevant statuses, so show it whenever there are actions or those links.
    const showDropdown = actions.length > 0 || ['Published', 'On hold', 'Action required', 'Auto marked as stale'].includes(status)

    // vsd-listing-ctas trims the MUI button padding to fit the photo column.
    return (
      <div className='vsd-listing-ctas flex items-center justify-start -ml-4'>
        {showDropdown
          ? <MoreOptionsDropdown
              listing={listing}
              actions={actions}
              label='Actions'
              isUpdating={() => this.setState({ isUpdating: true })}
            />
          : ''}

        <DeleteListingConfirmationDialog
          open={this.state.deleteDialogOpen}
          onCancel={this.handleCloseDeleteDialog}
          onConfirm={this.handleConfirmDelete}
          errorMessage={this.state.deleteError}
        />
      </div>
    )
  }

  render () {
    const listing = this.props.listing
    const { sellerReference, photo, title, sku, priceFormatted, createdAtFormatted, status, feedbackNotes, rejectionNotes, listingNotes, isBeingRelisted, deliverySize, isBoosted, relistedAtFormatted } = this.props.listing
    const showLoader = !isBeingRelisted && this.state.isUpdating && !this.state.displayErrorMessage
    const statusTagClassName = 'text-[12px] leading-[16px] text-center'

    return (
      <tr className='grid p-16 gap-x-16 grid-rows-[minmax(80px,max-content)] border-b border-midnight-black-20 hover:bg-[#FBFCFC]' style={{ gridTemplateColumns: this.state.gridTemplateColumns }}>
        {/* The status and the actions menu go under the photo, which is a lot shorter than the details beside it */}
        <td className='flex flex-col items-start gap-10'>
          <img alt={title} src={photo} style={{ maxWidth: '100%' }} />
          {/* Doing this here to avoid the rabbit hole of having a "Sponsored" listing status in the current setup */}
          {status === 'Published' && isBoosted
            ? <StatusTag content='Sponsored' className={statusTagClassName} />
            : <StatusTag content={status} className={statusTagClassName} />}
          {showLoader ? <Loader /> : this.renderCTAs(listing)}
        </td>
        <td>
          <div className='pb-10'> {title} </div>
          <div className='pb-10'> <strong> {priceFormatted} </strong></div>
          <div> Ref: {sellerReference} </div>
          <div> SKU: {sku} </div>
          <div className='pb-10'> Created: {createdAtFormatted} </div>
          <div> Delivery size: {deliverySize} </div>
        </td>

        {status === 'Action required' &&
          <td className='col-span-full mt-10 py-[5px] px-8 bg-punch-lime-20 rounded min-h-[32px]'>
            {!photo && !feedbackNotes && <div> Please add photos to this listing </div>}
            {!priceFormatted && !feedbackNotes && <div> Please add a price for this listing </div>}
            {feedbackNotes && <div> {feedbackNotes.notes} </div>}
            {listingNotes && <div> {listingNotes} </div>}
          </td>}
        {status === 'Disapproved' &&
          <td className='col-span-full mt-10 py-[5px] px-8 rounded min-h-[32px]' style={{ backgroundColor: 'rgb(255, 207, 219, 0.3)' }}>
            {rejectionNotes && rejectionNotes.notes}
          </td>}
        {relistedAtFormatted && <td className='col-span-full pt-10'> Relisted on {relistedAtFormatted} </td>}
        {isBeingRelisted && <td className='col-span-full pt-10'> This listing is being relisted. It can take a few minutes. </td>}
        {this.state.displayErrorMessage && <td className='col-span-full pt-10'> {this.state.errorMessage} </td>}
      </tr>
    )
  }
}

MobileTableRow.propTypes = {
  listing: PropTypes.object,
  canBoostListings: PropTypes.bool
}

const mapDispatchToProps = (dispatch) => {
  return {
    dispatch
  }
}

export default connect(mapDispatchToProps)(MobileTableRow)
